import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { CrossSellOfferDetails, CrossSellOfferResp, CrossSellView } from '@shared/model/cross-sell.model';
import { LenderService } from '@shared/service/lender/lender.service';
import { ProductImageComponent } from '@shared/shared.module';
import { CrossSellService } from './../../../service/cross-sell.service';

@Component({
  selector: 'dx-cross-sell-card',
  templateUrl: './cross-sell-card.component.html',
  styleUrls: ['./cross-sell-card.component.scss'],
  standalone: true,
  imports: [CommonModule, SharedUiLibraryModule, ProductImageComponent]
})
export class CrossSellCardComponent implements OnInit {
  // Inject dependencies using the new inject pattern
  public readonly lenderService = inject(LenderService);
  public readonly crossSellService = inject(CrossSellService);

  /** Getting ApplicationId as input */
  @Input() public loanNumber: string;

  // cross sell offers
  // Properties are optional for cross-sell card
  // If both these are null, we shouldnt even render this component
  @Input() public newCreditOffers?: CrossSellView;
  @Input() public refinanceOffers?: CrossSellView;

  /**
   * Loan data used for display within the component
   */
  // public item: CrossSell;
  /** holds full loan */
  public item: CrossSellView;

  /** Submitting to L360 cross sell opertunities */
  public objCrossSellOfferDetails: CrossSellOfferDetails;

  /** Trigger upload document close event */
  @Output() public ctaActionEmit = new EventEmitter<CrossSellOfferResp>();

  // Triggers full panel slide in panel for crossSell
  @Output() public openCrossSellPanel = new EventEmitter();

  /** URL to property Photo */
  // public imageUrl = '';

  /** call back msg change */
  public btnCallbackSuccess = false;
  public expirationDate = null;

  public isCollapsed = true;
  public stipulations = '';
  public stipulationList: string[] = [];
  public stipulationsShortened = false;

  // card type flag depending on the loanType of 'item'
  public isLoanCard: boolean;
  public isCreditCard: boolean;
  public isRefinanceCard: boolean;
  public icon: string;

  public openCrossSellDocModal({ oneClickOpen = false, bundle }: { oneClickOpen?: boolean; bundle?: any } = {}): void {
    this.openCrossSellPanel.emit({
      oneClickOpen,
      bundle,
      crossSellOffer: this.item,
      stipulationList: this.crossSellService.stipulationHelper(this.item?.offerDetails.stipulation)
    });
  }

  /**
   * On Init - set the item to the newCreditOffers or refinanceOffers
   */
  public ngOnInit() {
    // Prefer refinance if provided; else new credit; else remain undefined
    this.item = this.newCreditOffers || this.refinanceOffers;

    // If still no item, we are likely in a shallow test harness; safely exit.
    if (!this.item || !this.item.offerDetails) {
      return;
    }

    const offerDetails = this.item.offerDetails;
    this.stipulationList = this.crossSellService.stipulationHelper(offerDetails?.stipulation);
    this.stipulationsShortened = this.crossSellService.displayStipulationOnSlideInPanel(this.stipulationList);
    this.expirationDate = offerDetails?.expirationDate ? offerDetails.expirationDate : null;
    this.btnCallbackSuccess = offerDetails?.crossSellStatusDescription?.toLocaleLowerCase() === 'interested';
    this.item.offerDetails = this.crossSellService.stringReplaceCustomVar(offerDetails);
    this.stipulationList = this.crossSellService.stipulationListToDisplayHelper(this.stipulationList);
    this.setCrossSellCardType();
  }
  public expandCard = (): void => {
    this.isCollapsed = !this.isCollapsed;
  };

  public setCrossSellCardType(): void {
    if (!this.item || !this.item.offerDetails) {
      return;
    }
    if (this.newCreditOffers !== null && this.newCreditOffers !== undefined) {
      switch (this.item.offerDetails.loanType) {
        case 'Credit Card': {
          this.isCreditCard = true;
          this.icon = 'credit-card-with-confetti';
          break;
        }
        case 'Unsecured Loan': {
          this.isLoanCard = true;
          this.icon = 'coin-hand-with-confetti';
          break;
        }
        case 'Auto Direct':
        case 'Auto': {
          this.isRefinanceCard = true;
          this.icon = 'auto-loan-with-confetti';
          break;
        }
        default: {
          this.isLoanCard = true;
          this.icon = 'coin-hand-with-confetti';
          break;
        }
      }
    }
    if (this.refinanceOffers !== null && this.refinanceOffers !== undefined) {
      this.isRefinanceCard = true;
    }
  }

  /**
   * Accepts the offer and submits to L360
   */
  public acceptOffer(): void {
    this.crossSellService
      .startCrossSellApplication({
        applicationId: this.item.applicationId,
        crossSellId: +this.item.offerDetails.applicationCrossSellDetailId
      })
      .subscribe({
        next: (res) => {
          // assuming ApiResult shape with success or data presence
          if (res?.isSuccess) {
            if (res.data.offerAction === 'ExpressInterest') {
              this.btnCallbackSuccess = true;
            }
            res.data.fullCrossSellData = this.item;
            this.ctaActionEmit.emit(res.data);
          }
        },
        error: () => {
          // optionally surface error state; keep silent for now
        }
      });
  }
}
